import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Menu, Layout } from 'antd';
import PerfectScrollbar from 'react-perfect-scrollbar';
import useRolePermissions from '../../hook/useRolePermissions';

import { storeName as appStoreName } from '../../redux/models/app.model';
import { RootState } from '../../redux/store';
import { default as menuData } from './menuData';

import './style.scss';

const { Sider } = Layout;
const { SubMenu } = Menu;

const MenuLeft: FC = () => {
  const appDispatch = useDispatch()[appStoreName];
  const pathname = useSelector<RootState>((state) => state.router.location.pathname);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { collapsedState, theme } = useSelector<RootState>((state) => ({
    collapsedState: state.app.layoutState.menuCollapsed,
    theme: state.app.layoutState.themeLight ? 'light' : 'dark',
    settingsOpened: state.app.layoutState.settingsOpened,
  }));
  const [openKeys, setOpenKeys] = useState<Array<string>>(['']);
  const [selectedKeys, setSelectedKeys] = useState<string>('');

  const { hasAnyPermission } = useRolePermissions();

  const getPath = (data, id, parents = []) => {
    const items = data.reduce((result, entry) => {
      if (result.length > 0) {
        return result;
      }
      if (entry.url === id && selectedKeys === '') {
        return [entry].concat(parents);
      }
      if (entry.key === id && selectedKeys !== '') {
        return [entry].concat(parents);
      }
      if (entry.children) {
        const nested = getPath(entry.children, id, [entry].concat(parents));
        return nested || result;
      }
      return result;
    }, []);
    return items.length > 0 ? items : false;
  };

  const getActiveMenuItem = (items) => {
    const paths = getPath(items, selectedKeys || pathname);

    if (!paths) {
      return;
    }

    const activeMenuItem = paths.pop();
    let [...path] = paths;

    if (collapsedState) {
      path = [''];
    }

    setSelectedKeys(activeMenuItem ? activeMenuItem.key : '');
    setOpenKeys(activeMenuItem ? path.map((entry) => entry.key) : []);
  };

  useEffect(() => {
    getActiveMenuItem(menuData);
  }, [pathname]);

  const generateMenuItem = (item) => {
    const { key, title, url, icon, hidden } = item;

    if (hidden) {
      return null;
    }

    if (url) {
      return (
        <Menu.Item key={key} active={openKeys.includes(key)} icon={icon}>
          <Link to={url} onClick={() => setSelectedKeys(key)}>
            <span>{title}</span>
          </Link>
        </Menu.Item>
      );
    }

    return (
      <Menu.Item key={key} active={openKeys.includes(key)} icon={icon}>
        <Link to={url} onClick={() => setSelectedKeys(key)}>
          <span>{title}</span>
        </Link>
      </Menu.Item>
    );
  };

  const generateMenuPartitions = (items) => {
    return items
      .filter((menuItem) => {
        const { permissions = [] } = menuItem;
        return !permissions.length || hasAnyPermission(...permissions);
      })
      .map((menuItem) => {
      const { children, title, key, icon } = menuItem;

      if (children) {
        return (
          <SubMenu
            key={key}
            icon={<span className="anticon">{icon}</span>}
            title={title}
            onTitleClick={() => setSelectedKeys(key)}
          >
            {generateMenuPartitions(children)}
          </SubMenu>
        );
      }
      return generateMenuItem(menuItem);
    });
  };

  const menuItems = generateMenuPartitions(menuData);

  return (
    <Sider
      className="cvta-menu-left__wrapper"
      onCollapse={() => appDispatch.collapseMenu(!collapsedState)}
      collapsible
      collapsed={collapsedState}
      theme={theme}
    >
      <div className={`menu-left__logo-wrapper menu-left__logo-wrapper--${theme}`}>
        <a className="logo__image" href="/">
          <div className="sidebar-brand-icon">
            {theme === 'dark' ? (
              <img alt="Clik" src={`${process.env.PUBLIC_URL}/images/logo/logo-reverse.svg`} />
            ) : (
              <img alt="Clik" src={`${process.env.PUBLIC_URL}/images/logo/logo.svg`} />
            )}
          </div>
          {!collapsedState && (
            <div className="logo__text">
              <sup>Admin</sup>
            </div>
          )}
        </a>
      </div>
      <PerfectScrollbar style={{ height: 'calc(100vh - 112px)' }}>
        <Menu
          theme={theme}
          inlineCollapsed={collapsedState}
          mode="inline"
          defaultOpenKeys={[selectedKeys]}
          selectedKeys={openKeys}
          className="h-100"
        >
          {menuItems}
        </Menu>
      </PerfectScrollbar>
    </Sider>
  );
};

export { menuData };

export default MenuLeft;
